<template>
  <div class="kb-shell">
    <div v-if="loadError" class="empty">{{ loadError }}</div>
    <template v-else>
      <aside class="kb-rail">
        <div class="rail-head">
          <div class="title-row">
            <span class="title">龙头监控</span>
            <span class="badge">{{ railLeaders.length }}</span>
          </div>
        </div>
        <div v-if="loading" class="empty">加载监控池…</div>
        <div v-else-if="!leaders.length" class="empty">当前日期无监控池龙头</div>
        <div v-else-if="!railLeaders.length" class="empty">已入板块的股票不再显示</div>
        <div v-else class="rail-list">
          <LeaderColumn
            v-for="(leader, idx) in railLeaders"
            :key="leaderId(leader)"
            :leader="leader"
            :rank="idx + 1"
            :show-catchup="false"
            :selected="isSelected(leader)"
            checkable
            :return-meta="returnMeta"
            @select="onSelect"
          />
        </div>
      </aside>

      <section class="kb-main">
        <div class="sector-tabs">
          <button type="button" :class="{ on: !activeSectorId }" @click="openSector('')">概念匹配</button>
          <button
            v-for="item in builtSectors"
            :key="item.id"
            type="button"
            :class="{ on: activeSectorId === item.id }"
            @click="openSector(item.id)"
          >{{ item.label }}</button>
        </div>
        <header class="main-head">
          <div class="head-copy">
            <div class="head-ico"><i class="el-icon-aim" /></div>
            <div class="title-line">
              <div class="main-title">{{ memberMode ? kingdomLabel : '龙头概念匹配' }}</div>
              <div class="main-sub">{{ memberMode ? '已选中该板块在监控池里的股票，可继续勾选其他股票一起分析。' : '点击左侧龙头卡后，自动匹配相同概念，并按共性强度排序。' }}</div>
            </div>
          </div>
          <div v-if="primary" class="analysis">
            <div v-if="strongest" class="note note-blue">
              <div class="note-t"><i class="el-icon-circle-check" /> 共性最强：{{ strongest.name }} / {{ strongest.count }}只命中</div>
              <div class="note-d">在当前选中的龙头里，有 {{ strongest.count }} 只包含该概念</div>
            </div>
            <div v-else class="note note-blue">
              <div class="note-t">尚未形成可比较的相同概念</div>
              <div class="note-d">选中龙头的概念若只出现在本只股票上，不会计入共性。</div>
            </div>
            <div v-if="focusChip" class="note note-orange">
              <div class="note-t"><i class="el-icon-warning-outline" /> 可创建板块：{{ focusChip.name }}<template v-if="focusChip.name === (strongest && strongest.name)">（推荐）</template></div>
              <div class="note-d">
                <template v-if="focusChip.name === (strongest && strongest.name)">该概念在选中龙头里出现次数最多，建议作为主概念关注。</template>
                <template v-else>当前点选概念，命中 {{ focusChip.count }} 只。</template>
              </div>
              <div v-if="focusHolders.length" class="holders">
                包含该概念：
                <span v-for="h in focusHolders" :key="leaderId(h)">{{ h.name }}</span>
              </div>
            </div>
          </div>
        </header>

        <div v-if="!primary" class="panel empty">请从左侧选择龙头</div>
        <template v-else>
          <div class="picked">
            <div class="picked-list">
              <div v-for="(leader, idx) in pickedRows" :key="leaderId(leader)" class="picked-main">
                <div class="picked-body">
                  <span class="picked-k">{{ idx === 0 ? '当前选中：' : '' }}</span>
                  <b class="picked-name">{{ leader.name }}</b>
                  <span class="picked-code">{{ leader.code }}</span>
                  <button
                    v-if="canReplay(leader)"
                    type="button"
                    class="replay-btn"
                    :class="{ on: isReplayed(leader) }"
                    :title="isReplayed(leader) ? '再点一次取消复现' : '点一次后出现在左侧，可再加入一个板块'"
                    @click="markReplay(leader)"
                  >{{ isReplayed(leader) ? '取消复现' : '复现' }}</button>
                  <button
                    type="button"
                    class="eject-btn"
                    title="从板块拿掉，回到左侧龙头监控"
                    @click="ejectLeader(leader)"
                  >剔除</button>
                  <span v-if="boardText(leader)" class="pill pill-board">{{ boardText(leader) }}</span>
                  <span v-if="flagTomorrow(leader)" class="pill pill-hot">明日涨停即新高</span>
                  <span v-if="flagNewHigh(leader)" class="pill pill-hot">今日创新高</span>
                  <span v-if="flagLimitUp(leader)" class="pill pill-hot">今日涨停</span>
                  <span v-if="leader.closePrice != null" class="picked-px">{{ leader.closePrice }}</span>
                  <span class="picked-scope">{{ leader.changePctScope === 'CLOSE' ? '收盘' : '当前' }}</span>
                </div>
                <label class="status-pick" @click.stop>
                  <span>所属地位</span>
                  <el-select
                    :value="draftRole(leader)"
                    size="mini"
                    placeholder="请选择"
                    @change="(val) => setDraftRole(leader, val)"
                  >
                    <el-option v-for="role in roleDefs" :key="role.key" :label="role.label" :value="role.key" />
                  </el-select>
                </label>
                <div class="picked-extra">
                  <span v-if="roleOf(leader)" class="pill" :class="'pill-role-' + roleOf(leader)">{{ roleText(roleOf(leader)) }}</span>
                  <span v-if="idx === 0 && selectedLeaders.length > 1" class="pill pill-multi">已选中 {{ selectedLeaders.length }} 只</span>
                </div>
              </div>
            </div>
            <div class="picked-count">
              自动识别相同概念
              <b>{{ recognizedCount }}</b>
              个
            </div>
          </div>

          <div v-if="conceptsPending" class="panel hint">正在读取监控池概念（与补涨分析同源）…</div>

          <article class="panel">
            <div class="sec-h">
              <span class="sec-ico"><i class="el-icon-s-grid" /></span>
              <div class="title-line">
                <div class="sec-title">核心共性概念</div>
                <div class="sec-sub">{{ coreSubtitle }}</div>
              </div>
            </div>
            <div v-if="conceptsPending" class="sec-empty">正在统计选中龙头的概念…</div>
            <div v-else-if="!coreConcepts.length" class="sec-empty">暂无共性概念</div>
            <div v-else class="chips">
              <button
                v-for="chip in coreConcepts"
                :key="'c-' + chip.name"
                type="button"
                class="chip"
                :class="{ on: focusName === chip.name }"
                @click="focusName = chip.name"
              >
                <span>{{ chip.name }}</span>
                <em>{{ chip.count }}</em>
              </button>
            </div>
          </article>

          <article class="panel">
            <div class="sec-h">
              <span class="sec-ico sec-ico-orange"><i class="el-icon-share" /></span>
              <div class="title-line">
                <div class="sec-title">关联概念</div>
                <div class="sec-sub">{{ diffuseSubtitle }}</div>
              </div>
            </div>
            <div v-if="conceptsPending" class="sec-empty">正在统计选中龙头的概念…</div>
            <div v-else-if="!diffuseConcepts.length" class="sec-empty">没有仅出现 2 次的概念</div>
            <div v-else class="chips">
              <button
                v-for="chip in diffuseConcepts"
                :key="'d-' + chip.name"
                type="button"
                class="chip"
                :class="{ on: focusName === chip.name }"
                @click="focusName = chip.name"
              >
                <span>{{ chip.name }}</span>
                <em>{{ chip.count }}</em>
              </button>
            </div>
          </article>

          <section class="panel kingdom-config">
            <header class="cfg-head">
              <div class="cfg-title">
                <span class="cfg-ico"><i class="el-icon-s-operation" /></span>
                <div class="title-line">
                  <div class="sec-title">王国配置与操作</div>
                  <div class="sec-sub">确认主概念，副概念及王国归属</div>
                </div>
              </div>
              <div class="cfg-head-actions">
                <el-popover placement="bottom-end" width="340" trigger="click">
                  <p class="cfg-pop">主概念必选，用来确定王国归属。副概念最多 3 个。KPL 与 WC 可以同名，但是各自独立，请按实际题材选择。已经有同概念王国时，选「加入已有王国」。王国 TAB 名称默认取第一个点选龙头的前两个字，只影响本机显示。</p>
                  <button slot="reference" type="button" class="help-btn"><i class="el-icon-info" /> 概念配置说明</button>
                </el-popover>
                <button type="button" class="btn primary" @click="confirmCreateTab">{{ kingdomOp === 'join' ? '确认加入' : '确认创建并生成TAB' }}</button>
              </div>
            </header>

            <div v-if="kingdomOp !== 'join'" class="leader-concepts">
              <span>龙头概念</span>
              <div v-if="conceptsPending && !conceptOptions.length" class="mini-empty">正在读取概念…</div>
              <div v-else-if="!conceptOptions.length" class="mini-empty">当前龙头没有概念</div>
              <div v-else class="mini-chips">
                <button
                  v-for="opt in conceptOptions"
                  :key="'lc-' + opt.key"
                  type="button"
                  class="mini-chip"
                  :class="{ main: opt.key === mainConceptKey, sub: subConceptKeys.indexOf(opt.key) >= 0 }"
                  @click.stop="openConceptAssign($event, opt)"
                >{{ opt.name }}<i>{{ opt.source }}</i></button>
              </div>
            </div>

            <label v-if="kingdomOp !== 'join'" class="cfg-field">
              <span>主概念（必选）</span>
              <div class="main-field">
                <i class="crown">♔</i>
                <el-select
                  :value="mainConceptKey"
                  size="small"
                  filterable
                  placeholder="选择主概念"
                  @change="onMainConcept"
                >
                  <el-option v-for="opt in conceptOptions" :key="opt.key" :label="opt.label" :value="opt.key" />
                </el-select>
              </div>
            </label>

            <div v-if="kingdomOp !== 'join'" class="sub-grid">
              <label v-for="(key, idx) in subConceptKeys" :key="'sub-' + idx" class="cfg-field">
                <span>副概念 {{ idx + 1 }}（可选）</span>
                <el-select
                  :value="key"
                  size="small"
                  clearable
                  filterable
                  placeholder="选择副概念（可选）"
                  @change="(val) => onSubConcept(idx, val)"
                >
                  <el-option
                    v-for="opt in subOptions(idx)"
                    :key="opt.key"
                    :label="opt.label"
                    :value="opt.key"
                  />
                </el-select>
              </label>
            </div>

            <div class="op-row">
              <div class="cfg-field">
                <span>王国操作</span>
                <div class="ops">
                  <label><input v-model="kingdomOp" type="radio" value="create" /> 创建新王国</label>
                  <label><input v-model="kingdomOp" type="radio" value="join" /> 加入已有王国</label>
                </div>
                <el-select
                  v-if="kingdomOp === 'join'"
                  v-model="joinTargetId"
                  size="small"
                  class="join-target"
                >
                  <el-option v-for="item in builtSectors" :key="item.id" :label="item.label" :value="item.id" />
                </el-select>
              </div>
              <label v-if="kingdomOp !== 'join'" class="cfg-field">
                <span>王国TAB名称</span>
                <el-input
                  v-model="tabName"
                  size="small"
                  maxlength="30"
                  show-word-limit
                  placeholder="例如青岛"
                  @input="markTabNameTouched"
                />
                <small>默认取第一个点选龙头名称的前两个字，可手改，只影响本机显示。</small>
              </label>
            </div>

          </section>
        </template>
      </section>
      <el-dialog
        :visible.sync="roleDialogVisible"
        width="480px"
        append-to-body
        :close-on-click-modal="false"
        :title="'加入' + joinDialogLabel + '板块'"
      >
        <p class="role-tip">为新加入的股票选择标签。同一标签只保留一只，选中后会从原来的股票上移过来。</p>
        <div v-for="stock in pendingJoin" :key="stock.code" class="role-row">
          <div class="role-who"><b>{{ stock.name }}</b><span>{{ stock.code }}</span></div>
          <div class="role-opts">
            <button
              v-for="role in roleDefs"
              :key="stock.code + role.key"
              type="button"
              :class="{ on: pendingRoles[stock.code] === role.key }"
              @click="pickPendingRole(stock.code, role.key)"
            >
              {{ role.label }}
              <em v-if="roleHolderName(role.key) && pendingRoles[stock.code] !== role.key">当前 {{ roleHolderName(role.key) }}</em>
            </button>
          </div>
        </div>
        <span slot="footer">
          <el-button size="small" @click="roleDialogVisible = false">取消</el-button>
          <el-button size="small" type="primary" @click="confirmJoinRoles">确认加入</el-button>
        </span>
      </el-dialog>
      <div
        v-if="conceptAssign.visible"
        class="assign-menu"
        :style="{ left: conceptAssign.x + 'px', top: conceptAssign.y + 'px' }"
        @click.stop
      >
        <div class="assign-title">填入「{{ conceptAssign.label }}」</div>
        <button type="button" @click="assignConceptTo('main')">主概念</button>
        <button type="button" @click="assignConceptTo(0)">副概念1</button>
        <button type="button" @click="assignConceptTo(1)">副概念2</button>
        <button type="button" @click="assignConceptTo(2)">副概念3</button>
      </div>
    </template>
  </div>
</template>

<script>
import LeaderColumn from '../LeaderColumn.vue'
import { removeCodeFromAllRelayPools } from './themePropStorage'

const IGNORE_KEY = 'lcc.kingdom.ignoredConcepts.v1'
const ROLE_KEY = 'lcc.kingdom.roles.v1|culture'
const JOIN_KEY = 'lcc.kingdom.sectorJoins.v1'
const REPLAY_KEY = 'lcc.kingdom.replay.v1'
const ROLE_DEFS = [
  { key: 'leader', label: '总龙' },
  { key: 'trend', label: '趋势龙' },
  { key: 'board', label: '连板龙' }
]
const DEFAULT_ROLES = { '600825': 'leader', '601811': 'trend', '603096': 'board' }

function readRoles() {
  try {
    const text = localStorage.getItem(ROLE_KEY)
    if (text == null || text === '') return { ...DEFAULT_ROLES }
    const raw = JSON.parse(text)
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...DEFAULT_ROLES }
    const next = {}
    Object.keys(raw).forEach((code) => {
      const key = normCode6(code)
      const role = raw[code]
      if (!key) return
      next[key] = ROLE_DEFS.some((item) => item.key === role) ? role : ''
    })
    return next
  } catch (err) {
    return { ...DEFAULT_ROLES }
  }
}

function writeRoles(map) {
  try {
    localStorage.setItem(ROLE_KEY, JSON.stringify(map))
  } catch (err) {
    /* ignore */
  }
}

function readReplay() {
  try {
    const raw = JSON.parse(localStorage.getItem(REPLAY_KEY) || '[]')
    return Array.isArray(raw) ? raw.map((code) => normCode6(code)).filter((code) => code && code !== '000000') : []
  } catch (err) {
    return []
  }
}

function writeReplay(list) {
  try {
    localStorage.setItem(REPLAY_KEY, JSON.stringify(list))
  } catch (err) {
    /* ignore */
  }
}

function readJoins(roles) {
  let stored = {}
  try {
    const raw = JSON.parse(localStorage.getItem(JOIN_KEY) || '')
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) stored = raw
  } catch (err) {
    stored = {}
  }
  const next = {}
  const put = (code, sectorId) => {
    const key = normCode6(code)
    if (!key || key === '000000' || !sectorId) return
    const list = next[key] ? next[key].slice() : []
    if (list.indexOf(sectorId) < 0 && list.length < 2) list.push(sectorId)
    next[key] = list
  }
  Object.keys(stored).forEach((code) => {
    const list = stored[code]
    if (!Array.isArray(list)) return
    list.forEach((sectorId) => put(code, sectorId))
  })
  Object.keys(roles || {}).forEach((code) => put(code, 'culture'))
  return next
}

function writeJoins(map) {
  try {
    localStorage.setItem(JOIN_KEY, JSON.stringify(map))
  } catch (err) {
    /* ignore */
  }
}

function normCode6(code) {
  return String(code || '')
    .trim()
    .split('.')[0]
    .replace(/\D/g, '')
    .padStart(6, '0')
    .slice(-6)
}

function packBuildRoles(leaderCode, members) {
  const trendCodes = []
  const boardCodes = []
  ;(members || []).forEach((item) => {
    const code = normCode6(item && item.code)
    if (!code || code === '000000') return
    if (item.role === 'trend') trendCodes.push(code)
    if (item.role === 'board') boardCodes.push(code)
  })
  return {
    leader: normCode6(leaderCode) || '',
    trend: trendCodes[0] || '',
    board: boardCodes[0] || '',
    trendCodes,
    boardCodes
  }
}

function leaderId(leader) {
  return (leader && (leader.leaderId || leader.code)) || ''
}

function leaderNamePrefix(leader) {
  return Array.from(String((leader && leader.name) || '').trim()).slice(0, 2).join('')
}

function kingdomStorageId(name) {
  let hash = 0
  const text = String(name || '')
  for (let i = 0; i < text.length; i += 1) hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0
  return 'k' + (Math.abs(hash) || 1).toString(36)
}

function conceptNamesOf(items, merged) {
  const seen = new Set()
  const names = []
  const push = (raw) => {
    const name = String(raw || '').trim()
    if (!name || seen.has(name)) return
    seen.add(name)
    names.push(name)
  }
  ;(items || []).forEach((item) => push(item && item.name))
  if (!names.length) (merged || []).forEach(push)
  return names
}

function readIgnored() {
  try {
    const raw = JSON.parse(localStorage.getItem(IGNORE_KEY) || '[]')
    return Array.isArray(raw) ? raw.map((x) => String(x || '').trim()).filter(Boolean) : []
  } catch (err) {
    return []
  }
}

export default {
  name: 'KingdomBuildBoard',
  components: { LeaderColumn },
  props: {
    leaders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadError: { type: String, default: '' },
    returnMeta: { type: Object, default: () => ({}) },
    conceptsByCode: { type: Object, default: () => ({}) },
    conceptItemsByCode: { type: Object, default: () => ({}) },
    pins: { type: Object, default: () => ({}) },
    tradeDate: { type: String, default: '' },
    kingdoms: { type: Array, default: () => [] },
    showCulture: { type: Boolean, default: true }
  },
  data() {
    return {
      selectedIds: [],
      focusName: '',
      ignored: readIgnored(),
      dialogVisible: false,
      memberRoles: readRoles(),
      sectorJoins: readJoins(readRoles()),
      replayCodes: readReplay(),
      seededFor: '',
      roleDialogVisible: false,
      pendingRoles: {},
      roleDefs: ROLE_DEFS,
      activeSectorId: '',
      mainConceptKey: '',
      mainConceptTouched: false,
      subConceptKeys: ['', '', ''],
      kingdomOp: 'create',
      joinTargetId: 'culture',
      tabName: '',
      tabNameTouched: false,
      tabNameLock: false,
      conceptAssign: { visible: false, x: 0, y: 0, key: '', label: '' },
      draftRoles: {}
    }
  },
  computed: {
    builtSectors() {
      const extra = (this.kingdoms || [])
        .filter((item) => item && item.id && item.name)
        .map((item) => ({ id: item.id, label: item.name }))
      const culture = this.showCulture ? [{ id: 'culture', label: '文化传媒' }] : []
      return culture.concat(extra)
    },
    activeKingdom() {
      if (!this.activeSectorId || this.activeSectorId === 'culture') return null
      return (this.kingdoms || []).find((item) => item.id === this.activeSectorId) || null
    },
    sectorRoleMap() {
      const kingdom = this.activeKingdom
      if (!kingdom) return this.memberRoles
      const map = {}
      const roles = kingdom.roles && typeof kingdom.roles === 'object' ? kingdom.roles : {}
      Object.keys(roles).forEach((role) => {
        const code = normCode6(roles[role])
        if (code && code !== '000000') map[code] = role
      })
      ;(kingdom.members || []).forEach((member) => {
        const code = normCode6(member && member.code)
        if (!code || code === '000000' || map[code]) return
        map[code] = (member && member.role) || ''
      })
      return map
    },
    memberMode() {
      return !!this.activeSectorId
    },
    kingdomLabel() {
      const hit = this.builtSectors.find((item) => item.id === this.activeSectorId)
      return hit ? hit.label : ''
    },
    joinDialogLabel() {
      if (this.memberMode && this.kingdomLabel) return this.kingdomLabel
      const hit = this.builtSectors.find((item) => item.id === this.joinTargetId)
      return (hit && hit.label) || '王国'
    },
    submitLabel() {
      if (this.memberMode || this.kingdomOp === 'join') return '加入' + this.joinDialogLabel + '板块'
      return '加入板块战争'
    },
    conceptOptions() {
      const seen = new Set()
      const list = []
      ;(this.selectedLeaders || []).forEach((leader) => {
        const code = normCode6(leader.code || leader.leaderId)
        const items = ((this.conceptItemsByCode || {})[code]) || []
        items.forEach((item) => {
          const name = String((item && item.name) || '').trim()
          if (!name) return
          const source = item.source === 'KPL' || item.source === '固定' ? item.source : 'WC'
          const key = source + '|' + name
          if (seen.has(key)) return
          seen.add(key)
          list.push({ key, name, source, label: name + '（' + source + '）' })
        })
      })
      const rank = new Map()
      this.coreConcepts.concat(this.diffuseConcepts).forEach((chip, idx) => {
        if (!rank.has(chip.name)) rank.set(chip.name, idx)
      })
      list.sort((a, b) => {
        const ra = rank.has(a.name) ? rank.get(a.name) : 9999
        const rb = rank.has(b.name) ? rank.get(b.name) : 9999
        if (ra !== rb) return ra - rb
        if (a.name !== b.name) return String(a.name).localeCompare(String(b.name), 'zh-CN')
        return a.source === 'KPL' ? -1 : 1
      })
      return list
    },
    selectedLeaders() {
      const byId = Object.create(null)
      ;(this.leaders || []).forEach((leader) => {
        byId[leaderId(leader)] = leader
      })
      return this.selectedIds.map((id) => byId[id]).filter(Boolean)
    },
    primary() {
      return this.selectedLeaders[0] || null
    },
    pickedRows() {
      return this.selectedLeaders
    },
    railLeaders() {
      const live = new Set(this.builtSectors.map((item) => item.id))
      return (this.leaders || []).filter((leader) => {
        const count = this.sectorCount(leader, live)
        if (count <= 0) return true
        if (count >= 2) return false
        return this.isReplayed(leader)
      })
    },
    pendingJoin() {
      const map = this.memberMode ? this.sectorRoleMap : this.memberRoles
      return this.selectedLeaders.filter((leader) => !Object.prototype.hasOwnProperty.call(map, normCode6(leader.code || leader.leaderId)))
    },
    nameMap() {
      const map = Object.create(null)
      ;(this.leaders || []).forEach((leader) => {
        const code = normCode6(leader.code || leader.leaderId)
        if (!code) return
        const known = Object.prototype.hasOwnProperty.call(this.conceptItemsByCode || {}, code)
        if (!known) return
        map[leaderId(leader)] = conceptNamesOf(
          this.conceptItemsByCode[code],
          (this.conceptsByCode || {})[code]
        )
      })
      return map
    },
    conceptsPending() {
      return (this.leaders || []).some((leader) => {
        const code = normCode6(leader.code || leader.leaderId)
        return code && code !== '000000' && !Object.prototype.hasOwnProperty.call(this.conceptItemsByCode || {}, code)
      })
    },
    split() {
      const ignored = new Set(this.ignored)
      const selected = this.selectedLeaders
      const sets = selected
        .map((l) => this.nameMap[leaderId(l)])
        .filter((names) => Array.isArray(names))
      if (!selected.length || sets.length !== selected.length) {
        return { core: [], diffuse: [], recognized: 0 }
      }
      const countMap = new Map()
      sets.forEach((names) => {
        const seen = new Set()
        names.forEach((name) => {
          if (!name || seen.has(name) || ignored.has(name)) return
          seen.add(name)
          countMap.set(name, (countMap.get(name) || 0) + 1)
        })
      })
      const ranked = [...countMap.entries()]
        .map(([name, count]) => ({ name, count }))
        .filter((item) => item.count >= 2)
        .sort((a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name), 'zh-CN'))
      const core = ranked.filter((item) => item.count >= 3)
      const diffuse = ranked.filter((item) => item.count < 3)
      if (core.length || !ranked.length) {
        return { core, diffuse, recognized: ranked.length }
      }
      const top = ranked[0].count
      return {
        core: ranked.filter((item) => item.count === top),
        diffuse: ranked.filter((item) => item.count !== top),
        recognized: ranked.length
      }
    },
    coreConcepts() {
      return this.split.core
    },
    diffuseConcepts() {
      return this.split.diffuse
    },
    recognizedCount() {
      return this.split.recognized
    },
    coreSubtitle() {
      return '选中龙头里出现不少于 3 次的概念，按次数从高到低'
    },
    diffuseSubtitle() {
      return '选中龙头里出现 2 次的概念，计数方式与补涨概念分析相同'
    },
    strongest() {
      return this.coreConcepts[0] || this.diffuseConcepts[0] || null
    },
    focusChip() {
      const name = this.focusName
      const all = this.coreConcepts.concat(this.diffuseConcepts)
      if (name) {
        const hit = all.find((c) => c.name === name)
        if (hit) return hit
      }
      return this.strongest
    },
    focusNameResolved() {
      return (this.focusChip && this.focusChip.name) || ''
    },
    focusHolders() {
      const name = this.focusNameResolved
      if (!name) return []
      return this.selectedLeaders.filter((leader) => {
        const names = this.nameMap[leaderId(leader)] || []
        return names.indexOf(name) >= 0
      })
    }
  },
  watch: {
    leaders: {
      immediate: true,
      handler(list) {
        const rows = list || []
        rows.forEach((leader) => this.$emit('ensure-concepts', leader))
        if (this.seedMembers(rows)) return
        const alive = new Set(rows.map((l) => leaderId(l)).filter(Boolean))
        const kept = this.selectedIds.filter((id) => alive.has(id))
        if (kept.length !== this.selectedIds.length) this.selectedIds = kept
      }
    },
    primary: {
      immediate: true,
      handler() {
        this.applyDefaultTabName()
      }
    },
    focusNameResolved(name) {
      if (name && name !== this.focusName) this.focusName = name
      this.syncMainFromFocus()
    },
    conceptOptions() {
      this.syncMainFromFocus()
    },
    loading(val) {
      if (!val) this.seedMembers(this.leaders || [])
    },
    kingdoms() {
      this.pruneStaleJoins()
      this.reconcileClearedRoles()
    },
    showCulture() {
      this.pruneStaleJoins()
    }
  },
  mounted() {
    document.addEventListener('click', this.closeConceptAssign)
    this.pruneStaleJoins()
    this.reconcileClearedRoles()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeConceptAssign)
  },
  methods: {
    leaderId,
    isSelected(leader) {
      return this.selectedIds.indexOf(leaderId(leader)) >= 0
    },
    sectorCount(leader, liveSet) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      if (!code || code === '000000') return 0
      const live = liveSet || new Set(this.builtSectors.map((item) => item.id))
      const list = (this.sectorJoins[code] || []).filter((id) => live.has(id))
      if (list.length) return Math.min(list.length, 2)
      if (this.showCulture && this.memberRoles[code]) return 1
      return 0
    },
    releaseJoinOnly(code, sectorId) {
      const key = normCode6(code)
      if (!key || key === '000000' || !sectorId) return
      const joins = { ...this.sectorJoins }
      const prev = Array.isArray(joins[key]) ? joins[key] : []
      const list = prev.filter((id) => id !== sectorId)
      if (list.length === prev.length) return
      if (list.length) joins[key] = list
      else delete joins[key]
      this.sectorJoins = joins
      writeJoins(joins)
    },
    reconcileClearedRoles() {
      const prefix = 'lcc.kingdom.roleOverride.v1|'
      ;(this.kingdoms || []).forEach((kingdom) => {
        if (!kingdom || !kingdom.id) return
        let raw = null
        try {
          raw = JSON.parse(localStorage.getItem(prefix + kingdom.id) || 'null')
        } catch (err) {
          raw = null
        }
        if (!raw || typeof raw !== 'object') return
        const roles = { leader: '', trend: '', board: '', ...(kingdom.roles || {}) }
        const dropped = []
        ;['trend', 'board'].forEach((key) => {
          if (!(raw[key] && raw[key].cleared)) return
          const code = normCode6(roles[key] || (raw[key] && raw[key].code))
          if (!code || code === '000000') return
          dropped.push(code)
          roles[key] = ''
          this.releaseJoinOnly(code, kingdom.id)
          removeCodeFromAllRelayPools(code)
        })
        if (!dropped.length) return
        const members = (kingdom.members || []).filter((item) => dropped.indexOf(normCode6(item && item.code)) < 0)
        this.$emit('update-kingdom', { id: kingdom.id, roles, members })
      })
    },
    pruneStaleJoins() {
      const live = new Set(this.builtSectors.map((item) => item.id))
      const joins = { ...this.sectorJoins }
      let changed = false
      Object.keys(joins).forEach((code) => {
        const list = (joins[code] || []).filter((id) => live.has(id))
        if (list.length === (joins[code] || []).length) return
        changed = true
        if (list.length) joins[code] = list
        else delete joins[code]
      })
      if (!changed) return
      this.sectorJoins = joins
      writeJoins(joins)
    },
    canReplay(leader) {
      return this.sectorCount(leader) < 2
    },
    isReplayed(leader) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      return !!(code && this.replayCodes.indexOf(code) >= 0)
    },
    markReplay(leader) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      if (!code || code === '000000' || this.sectorCount(leader) >= 2) return
      const idx = this.replayCodes.indexOf(code)
      this.replayCodes = idx >= 0
        ? this.replayCodes.filter((item) => item !== code)
        : this.replayCodes.concat([code])
      writeReplay(this.replayCodes)
    },
    ejectLeader(leader) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      if (!code || code === '000000') return
      if (this.memberMode && this.activeSectorId && this.activeSectorId !== 'culture') {
        const map = { ...this.sectorRoleMap }
        delete map[code]
        this.persistKingdomRoles(this.activeSectorId, map)
        const joins = { ...this.sectorJoins }
        const list = Array.isArray(joins[code]) ? joins[code].filter((id) => id !== this.activeSectorId) : []
        if (list.length) joins[code] = list
        else delete joins[code]
        this.sectorJoins = joins
        writeJoins(joins)
        const id = leaderId(leader)
        const idx = this.selectedIds.indexOf(id)
        if (idx >= 0) this.selectedIds.splice(idx, 1)
        if (!this.selectedIds.length) this.focusName = ''
        this.$message.success(`${(leader && leader.name) || code} 已回到左侧龙头监控`)
        return
      }
      const joins = { ...this.sectorJoins }
      delete joins[code]
      this.sectorJoins = joins
      writeJoins(joins)
      if (Object.prototype.hasOwnProperty.call(this.memberRoles, code)) {
        const roles = { ...this.memberRoles }
        delete roles[code]
        this.memberRoles = roles
        writeRoles(roles)
      }
      if (this.replayCodes.indexOf(code) >= 0) {
        this.replayCodes = this.replayCodes.filter((item) => item !== code)
        writeReplay(this.replayCodes)
      }
      if (Object.prototype.hasOwnProperty.call(this.draftRoles, code)) {
        const drafts = { ...this.draftRoles }
        delete drafts[code]
        this.draftRoles = drafts
      }
      const id = leaderId(leader)
      const idx = this.selectedIds.indexOf(id)
      if (idx >= 0) this.selectedIds.splice(idx, 1)
      if (!this.selectedIds.length) this.focusName = ''
      const name = (leader && leader.name) || code
      this.$message.success(`${name} 已回到左侧龙头监控`)
    },
    rememberSector(code, sectorId) {
      const key = normCode6(code)
      if (!key || key === '000000' || !sectorId) return
      const next = { ...this.sectorJoins }
      const list = Array.isArray(next[key]) ? next[key].slice() : []
      if (list.indexOf(sectorId) < 0 && list.length < 2) list.push(sectorId)
      next[key] = list
      this.sectorJoins = next
      writeJoins(next)
    },
    onSelect(leader) {
      const id = leaderId(leader)
      if (!id) return
      const idx = this.selectedIds.indexOf(id)
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1)
        this.focusName = ''
        return
      }
      this.selectedIds.push(id)
      this.focusName = ''
    },
    boardText(leader) {
      return leader.boardTierLabel || leader.boardStageLabel || leader.nDayMBoard || ''
    },
    flagNewHigh(leader) {
      const se = leader.stateEvidence
      const f = se && se.priceFacts && se.priceFacts.NEW_HIGH_TODAY
      if (f && (f.value === 'YES' || f.value === true)) return true
      const pe = leader.priceEvidence
      return !!(pe && (pe.newHigh === 'YES' || pe.newHigh === true))
    },
    flagLimitUp(leader) {
      const se = leader.stateEvidence
      const f = se && se.structureFacts && se.structureFacts.LIMIT_UP_TODAY
      if (f && (f.value === 'YES' || f.value === true)) return true
      if (leader.currentBoard != null && Number(leader.currentBoard) > 0) return true
      const text = this.boardText(leader)
      return !!(text && /^\d+天\d+板$/.test(text))
    },
    flagTomorrow(leader) {
      const se = leader.stateEvidence
      const f = se && se.priceFacts && se.priceFacts.TOMORROW_LU_IS_NEW_HIGH
      return !!(f && (f.value === 'YES' || f.value === true))
    },
    clearSelection() {
      this.selectedIds = []
      this.focusName = ''
      this.dialogVisible = false
    },
    openSector(id) {
      if (this.activeSectorId === id) return
      this.activeSectorId = id
      this.selectedIds = []
      this.focusName = ''
      this.tabNameTouched = false
      this.tabName = ''
      this.dialogVisible = false
      this.roleDialogVisible = false
      this.seededFor = ''
      this.seedMembers(this.leaders || [])
    },
    removeSelected(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx >= 0) this.selectedIds.splice(idx, 1)
      if (!this.selectedIds.length) this.dialogVisible = false
    },
    joinSectorWar() {
      this.applySelectedRoles()
    },
    confirmCreateTab() {
      this.applySelectedRoles(this.kingdomOp !== 'join')
    },
    submitKingdom() {
      this.applySelectedRoles()
    },
    draftRole(leader) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      if (!code) return ''
      if (Object.prototype.hasOwnProperty.call(this.draftRoles, code)) return this.draftRoles[code]
      return this.sectorRoleMap[code] || ''
    },
    setDraftRole(leader, roleKey) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      if (!code) return
      const next = { ...this.draftRoles }
      if (roleKey === 'leader') {
        Object.keys(next).forEach((item) => {
          if (item !== code && next[item] === roleKey) next[item] = ''
        })
        Object.keys(this.sectorRoleMap).forEach((item) => {
          if (item !== code && this.sectorRoleMap[item] === roleKey && !Object.prototype.hasOwnProperty.call(next, item)) next[item] = ''
        })
      }
      next[code] = roleKey || ''
      this.draftRoles = next
    },
    applySelectedRoles(forceCreate) {
      const joining = this.selectedLeaders.slice()
      if (!joining.length) {
        this.$message.warning('请先选择至少一只股票')
        return
      }
      const missing = joining.filter((leader) => !this.draftRole(leader))
      if (missing.length) {
        this.$message.warning('请在当前选中处选择所属地位：总龙、趋势龙或连板龙')
        return
      }
      const tabName = String(this.tabName || '').trim()
      if (forceCreate || (!this.memberMode && this.kingdomOp !== 'join')) {
        if (!this.mainConceptKey) {
          this.$message.warning('请先选择主概念')
          return
        }
        if (!tabName) {
          this.$message.warning('请填写王国TAB名称')
          return
        }
        const members = []
        let leaderCode = ''
        joining.forEach((leader) => {
          const code = normCode6(leader.code || leader.leaderId)
          const role = this.draftRole(leader)
          if (role === 'leader') leaderCode = code
          members.push({ code, name: leader.name || '', role })
        })
        const roles = packBuildRoles(leaderCode, members)
        const id = kingdomStorageId(tabName)
        const hit = this.conceptOptions.find((item) => item.key === this.mainConceptKey)
        const subHits = this.subConceptKeys.map((key) => this.conceptOptions.find((item) => item.key === key) || null)
        const subConceptNames = subHits.filter(Boolean).map((item) => item.name)
        const subConceptLabels = subHits.map((item) => (item ? item.label : ''))
        joining.forEach((leader) => this.rememberSector(leader.code || leader.leaderId, id))
        this.$emit('create-kingdom', {
          id,
          name: tabName,
          mainConceptName: hit ? hit.name : '',
          mainConceptLabel: hit ? hit.label : '',
          subConceptNames,
          subConceptLabels,
          roles,
          members
        })
        return
      }
      const sectorId = (this.memberMode && this.activeSectorId) || this.joinTargetId || 'culture'
      const next = sectorId === 'culture' ? { ...this.memberRoles } : { ...this.sectorRoleMap }
      joining.forEach((leader) => {
        const code = normCode6(leader.code || leader.leaderId)
        const role = this.draftRole(leader)
        if (role === 'leader') {
          Object.keys(next).forEach((item) => {
            if (item !== code && next[item] === role) next[item] = ''
          })
        }
        next[code] = role
      })
      const names = joining.map((leader) => `${leader.name}·${this.roleText(this.draftRole(leader))}`).join('、')
      if (sectorId !== 'culture') {
        this.persistKingdomRoles(sectorId, next)
        joining.forEach((leader) => this.rememberSector(leader.code || leader.leaderId, sectorId))
        this.$message.success(`已记入${this.joinDialogLabel}：${names}`)
        return
      }
      this.memberRoles = next
      writeRoles(next)
      joining.forEach((leader) => this.rememberSector(leader.code || leader.leaderId, sectorId))
      this.$message.success(`已记入${this.joinDialogLabel}：${names}`)
      if (!this.memberMode && this.joinTargetId === 'culture') this.$emit('open-kingdom', 'kingdomDetail:culture-kpl')
    },
    openRoleJoin() {
      if (!this.selectedLeaders.length) {
        this.$message.warning('请先选择至少一只股票')
        return
      }
      if (!this.pendingJoin.length) {
        this.$message.info(`当前选中的都已在${this.joinDialogLabel}里。请再勾选要加入的股票`)
        return
      }
      const pending = {}
      this.pendingJoin.forEach((leader) => {
        pending[normCode6(leader.code || leader.leaderId)] = ''
      })
      this.pendingRoles = pending
      this.roleDialogVisible = true
    },
    onMainConcept(key) {
      this.mainConceptTouched = true
      this.mainConceptKey = key
      const hit = this.conceptOptions.find((item) => item.key === key)
      if (hit) this.focusName = hit.name
      this.subConceptKeys = this.subConceptKeys.map((item) => (item === key ? '' : item))
    },
    onSubConcept(idx, val) {
      const next = this.subConceptKeys.slice()
      next[idx] = val || ''
      this.subConceptKeys = next
    },
    openConceptAssign(event, opt) {
      const rect = event.currentTarget.getBoundingClientRect()
      this.conceptAssign = {
        visible: true,
        x: rect.left,
        y: rect.bottom + 4,
        key: opt.key,
        label: opt.label
      }
    },
    closeConceptAssign() {
      if (!this.conceptAssign.visible) return
      this.conceptAssign = { visible: false, x: 0, y: 0, key: '', label: '' }
    },
    assignConceptTo(slot) {
      const key = this.conceptAssign.key
      this.closeConceptAssign()
      if (!key) return
      if (slot === 'main') {
        this.onMainConcept(key)
        return
      }
      if (this.mainConceptKey === key) {
        this.mainConceptTouched = true
        this.mainConceptKey = ''
      }
      const next = this.subConceptKeys.slice()
      next.forEach((item, idx) => {
        if (idx !== slot && item === key) next[idx] = ''
      })
      next[slot] = key
      this.subConceptKeys = next
    },
    subOptions(idx) {
      const used = new Set([this.mainConceptKey].concat(this.subConceptKeys.filter((item, i) => i !== idx && item)))
      return this.conceptOptions.filter((item) => !used.has(item.key))
    },
    syncMainFromFocus() {
      if (this.mainConceptTouched) return
      const name = this.focusNameResolved
      if (!name || !this.conceptOptions.length) return
      const hit = this.conceptOptions.find((item) => item.name === name && item.source === 'KPL')
        || this.conceptOptions.find((item) => item.name === name)
      if (!hit || hit.key === this.mainConceptKey) return
      this.mainConceptKey = hit.key
    },
    applyDefaultTabName() {
      if (this.tabNameTouched) return
      const next = leaderNamePrefix(this.primary)
      if (next === this.tabName) return
      this.tabNameLock = true
      this.tabName = next
      this.$nextTick(() => {
        this.tabNameLock = false
      })
    },
    markTabNameTouched() {
      if (this.tabNameLock) return
      this.tabNameTouched = true
    },
    applyMainConcept() {
      const chip = this.focusChip
      if (!chip) {
        this.$message.warning('请先点选一个概念')
        return
      }
      const hit = this.conceptOptions.find((item) => item.name === chip.name && item.source === 'KPL')
        || this.conceptOptions.find((item) => item.name === chip.name)
      if (hit) this.onMainConcept(hit.key)
      this.setAsPrimary()
    },
    roleOf(leader) {
      if (!this.memberMode) return ''
      return this.sectorRoleMap[normCode6(leader && (leader.code || leader.leaderId))] || ''
    },
    roleText(key) {
      const hit = ROLE_DEFS.find((item) => item.key === key)
      return hit ? hit.label : ''
    },
    roleHolderName(roleKey) {
      const code = Object.keys(this.memberRoles).find((item) => this.memberRoles[item] === roleKey)
      if (!code) return ''
      const leader = (this.leaders || []).find((row) => normCode6(row.code || row.leaderId) === code)
      return (leader && leader.name) || code
    },
    pickPendingRole(code, roleKey) {
      const next = { ...this.pendingRoles }
      if (roleKey === 'leader') {
        Object.keys(next).forEach((item) => {
          if (item !== code && next[item] === roleKey) next[item] = ''
        })
      }
      next[code] = roleKey
      this.pendingRoles = next
    },
    confirmJoinRoles() {
      const joining = this.pendingJoin.slice()
      const missing = joining.filter((leader) => !this.pendingRoles[normCode6(leader.code || leader.leaderId)])
      if (missing.length) {
        this.$message.warning('请为每只新股票选择龙头、趋势龙或连板龙')
        return
      }
      const sectorId = (this.memberMode && this.activeSectorId) || this.joinTargetId || 'culture'
      const next = sectorId === 'culture' ? { ...this.memberRoles } : { ...this.sectorRoleMap }
      joining.forEach((leader) => {
        const code = normCode6(leader.code || leader.leaderId)
        const role = this.pendingRoles[code]
        if (role === 'leader') {
          Object.keys(next).forEach((item) => {
            if (item !== code && next[item] === role) next[item] = ''
          })
        }
        next[code] = role
      })
      const names = joining.map((leader) => `${leader.name}·${this.roleText(next[normCode6(leader.code || leader.leaderId)])}`).join('、')
      if (sectorId !== 'culture') {
        this.persistKingdomRoles(sectorId, next)
      } else {
        this.memberRoles = next
        writeRoles(next)
      }
      joining.forEach((leader) => this.rememberSector(leader.code || leader.leaderId, sectorId))
      this.roleDialogVisible = false
      this.$message.success(`已加入${this.joinDialogLabel}：${names}`)
    },
    seedMembers(rows) {
      if (!this.memberMode || this.loading) return false
      const stamp = this.tradeDate || 'current'
      if (this.seededFor === stamp) return false
      const list = rows || []
      if (!list.length) return false
      this.selectedIds = this.memberIdsIn(list)
      this.focusName = ''
      this.seededFor = stamp
      return true
    },
    memberIdsIn(rows) {
      const byCode = Object.create(null)
      ;(rows || []).forEach((leader) => {
        const code = normCode6(leader.code || leader.leaderId)
        const id = leaderId(leader)
        if (code && id) byCode[code] = id
      })
      return Object.keys(this.sectorRoleMap).map((code) => byCode[code]).filter(Boolean)
    },
    persistKingdomRoles(sectorId, roleMap) {
      const knownMembers = (this.activeKingdom && this.activeSectorId === sectorId && this.activeKingdom.members) || []
      const members = []
      let leaderCode = ''
      Object.keys(roleMap || {}).forEach((code) => {
        const role = roleMap[code]
        if (!role) return
        if (role === 'leader') leaderCode = code
        const leader = (this.leaders || []).find((item) => normCode6(item.code || item.leaderId) === code)
        const known = knownMembers.find((item) => normCode6(item && item.code) === code)
        members.push({
          code,
          name: (leader && leader.name) || (known && known.name) || '',
          role
        })
      })
      this.$emit('update-kingdom', { id: sectorId, roles: packBuildRoles(leaderCode, members), members })
    },
    setAsPrimary() {
      const chip = this.focusChip
      if (!chip) return
      this.selectedLeaders.forEach((leader) => {
        this.$emit('set-manual-attr', leader.leaderId || leader.code, chip.name)
      })
      this.$message.success(`已将「${chip.name}」设为选中龙头的人工主概念（仅本机）`)
    },
    ignoreFocus() {
      const chip = this.focusChip
      if (!chip || this.ignored.indexOf(chip.name) >= 0) return
      this.ignored = this.ignored.concat([chip.name])
      this.focusName = ''
      try {
        localStorage.setItem(IGNORE_KEY, JSON.stringify(this.ignored))
      } catch (err) {
        /* ignore */
      }
    },
    restoreIgnored() {
      this.ignored = []
      try {
        localStorage.removeItem(IGNORE_KEY)
      } catch (err) {
        /* ignore */
      }
    }
  }
}
</script>

<style scoped lang="scss">
.kb-shell {
  display: grid;
  grid-template-columns: minmax(240px, 22%) minmax(0, 1fr);
  gap: 12px;
  height: calc(100vh - 230px);
  min-height: 520px;
  margin-top: 10px;
}
.kb-rail,
.kb-main {
  min-height: 0;
  overflow: auto;
}
.kb-rail {
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
}
.rail-head {
  padding: 12px 12px 8px;
  border-bottom: 1px solid #e8eef8;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.title-row { display: flex; align-items: center; gap: 8px; }
.title { font-size: 15px; font-weight: 800; }
.badge {
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #e8f1ff;
  color: #1a73e8;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sub { margin-top: 4px; font-size: 11px; color: #8aa0c2; }
.sel-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
  font-size: 12px;
  color: #35598c;
  button {
    border: 1px solid #d7e4f6;
    background: #fff;
    border-radius: 8px;
    padding: 2px 8px;
    cursor: pointer;
    &:disabled { opacity: 0.45; cursor: not-allowed; }
  }
  .join { background: #1a73e8; color: #fff; border-color: #1a73e8; }
}
.rail-list {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.kb-main { display: flex; flex-direction: column; gap: 10px; padding-right: 2px; }
.sector-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  button {
    border: 1px solid #d7e4f6;
    background: #fff;
    color: #3d5270;
    border-radius: 16px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
    &.on { background: #1a73e8; border-color: #1a73e8; color: #fff; font-weight: 700; }
  }
}
.main-head { display: flex; gap: 12px; align-items: flex-start; justify-content: space-between; padding: 2px 2px 0; }
.head-copy { display: flex; gap: 10px; align-items: center; flex-shrink: 0; min-width: 0; }
.title-line { display: flex; align-items: baseline; gap: 8px; min-width: 0; flex-wrap: nowrap; }
.head-ico {
  width: 28px; height: 28px; border-radius: 8px;
  background: #e8f1ff; color: #1a73e8;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.main-title { font-size: 16px; font-weight: 800; white-space: nowrap; }
.main-sub { font-size: 12px; color: #8aa0c2; white-space: nowrap; }
.panel {
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 14px;
  padding: 12px 14px;
}
.picked {
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.picked-list {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  column-gap: 12px;
  row-gap: 8px;
  align-items: center;
  min-width: 0;
  flex: 1;
}
.picked-main { display: contents; }
.picked-body { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; min-width: 0; }
.picked-extra { display: flex; align-items: center; gap: 6px; min-width: 88px; }
.status-pick {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  font-size: 12px;
  color: #5b6b82;
  white-space: nowrap;
}
.status-pick :deep(.el-select) { width: 108px; }
.picked-k { color: #5b6b82; font-size: 13px; display: inline-block; min-width: 72px; }
.picked-name { font-size: 16px; margin-right: 6px; }
.picked-code { color: #8aa0c2; font-size: 12px; margin-right: 8px; }
.replay-btn {
  margin-right: 8px;
  height: 20px;
  padding: 0 8px;
  border: 1px solid #f5b4b4;
  border-radius: 10px;
  background: #fff5f5;
  color: #d64545;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
}
.replay-btn.on {
  border-color: #1a73e8;
  background: #e8f1ff;
  color: #1a73e8;
}
.eject-btn {
  margin-right: 8px;
  height: 20px;
  padding: 0 8px;
  border: 1px solid #d7e4f6;
  border-radius: 10px;
  background: #f7faff;
  color: #5b6b82;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
}
.eject-btn:hover {
  border-color: #1a73e8;
  color: #1a73e8;
}
.picked-px { margin-left: 8px; font-weight: 800; color: #1a2b3c; }
.picked-scope { margin-left: 6px; font-size: 12px; color: #8aa0c2; }
.picked-count { color: #5b6b82; font-size: 13px; white-space: nowrap; b { color: #1a73e8; font-size: 22px; margin: 0 4px; } }
.pill {
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
}
.pill-board { background: #e8f1ff; color: #1a73e8; }
.pill-hot { background: #fff1f0; color: #e24b4b; }
.pill-multi { background: #f3e8ff; color: #7a3ec8; }
.pill-role-leader { background: #fff6e0; color: #b8860b; }
.pill-role-trend { background: #e8f1ff; color: #1a73e8; }
.pill-role-board { background: #fde8ee; color: #c43b5a; }
.role-tip { margin: 0 0 12px; color: #5b6b82; font-size: 13px; }
.role-row { padding: 10px 0; border-top: 1px solid #e8eef8; }
.role-who { display: flex; gap: 8px; align-items: baseline; margin-bottom: 8px; span { color: #8aa0c2; font-size: 12px; } }
.role-opts { display: flex; gap: 8px; flex-wrap: wrap; }
.role-opts button {
  border: 1px solid #d7e4f6;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  color: #1a2b3c;
  em { display: block; font-style: normal; color: #8aa0c2; font-size: 11px; }
  &.on { background: #1a73e8; border-color: #1a73e8; color: #fff; em { color: rgba(255,255,255,.8); } }
}
.sec-h { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; }
.sec-ico {
  width: 26px; height: 26px; border-radius: 8px;
  background: #e8f1ff; color: #1a73e8;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.sec-ico-orange { background: #fff4e8; color: #e07a1a; }
.sec-title { font-size: 14px; font-weight: 800; white-space: nowrap; }
.sec-sub { font-size: 12px; color: #8aa0c2; font-weight: 400; white-space: nowrap; }
.sec-empty, .empty, .hint { color: #8aa0c2; font-size: 12px; padding: 8px 0; }
.chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  border: 1px solid #e3ebf6;
  background: #f8fbff;
  border-radius: 10px;
  padding: 8px 10px 8px 12px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #1a2b3c;
  font-size: 13px;
  em {
    font-style: normal;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #1a73e8;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  &.on { border-color: #1a73e8; box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15); }
}
.advice-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 10px;
}
.analysis {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex: 1;
  min-width: 0;
}
.note { border-radius: 10px; padding: 8px 10px; flex: 1; min-width: 0; max-width: 360px; }
.note-blue { background: #f3f8ff; }
.note-orange { background: #fff8ee; }
.note-t { font-weight: 800; font-size: 13px; margin-bottom: 4px; }
.note-d, .holders { font-size: 12px; color: #5b6b82; line-height: 1.5; }
.holders span {
  display: inline-block;
  margin: 4px 6px 0 0;
  padding: 1px 6px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #f0d7b0;
}
.actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid transparent;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
}
.btn-primary { background: #1a73e8; color: #fff; }
.btn-ghost { background: #fff; color: #1a73e8; border-color: #1a73e8; }
.btn-text { background: transparent; color: #8aa0c2; }
.side-help p, .side-help li { margin: 0; font-size: 12px; color: #5b6b82; line-height: 1.55; }
.help-h { font-weight: 800; font-size: 13px; margin-bottom: 4px; }
.help-block + .help-block { margin-top: 12px; }
.side-help ul { margin: 0; padding-left: 16px; }
.restore {
  margin-top: 12px;
  border: 0;
  background: transparent;
  color: #1a73e8;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}
.multi-foot {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f3f8ff;
  border: 1px solid #d7e4f6;
  border-radius: 12px;
  padding: 10px 12px;
  color: #35598c;
  font-size: 12px;
  i { color: #1a73e8; margin-top: 2px; }
  b { display: block; margin-bottom: 2px; }
}
.cfg-pop { margin: 0; color: #3d5270; font-size: 13px; line-height: 1.6; }
.kingdom-config { display: flex; flex-direction: column; gap: 12px; }
.cfg-head { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
.cfg-title { display: flex; gap: 8px; align-items: center; min-width: 0; }
.cfg-head-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.cfg-ico {
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  background: #e8f1ff; color: #1a73e8;
  display: flex; align-items: center; justify-content: center;
}
.help-btn {
  border: 0; background: #e8f1ff; color: #1a73e8; border-radius: 14px;
  padding: 4px 10px; cursor: pointer; font-size: 12px; white-space: nowrap;
}
.cfg-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; font-size: 12px; color: #5b6b82; }
.cfg-field :deep(.el-select),
.cfg-field :deep(.el-input) { width: 100%; }
.main-field { position: relative; }
.crown {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  z-index: 2; color: #e0a100; font-style: normal; pointer-events: none;
}
.main-field :deep(.el-input__inner) { padding-left: 28px; }
.sub-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.op-row { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 16px; }
.ops { display: flex; gap: 16px; align-items: center; min-height: 32px; color: #1a2b3c; font-size: 13px; }
.ops input { margin-right: 4px; }
.join-target { margin-top: 6px; }
.cfg-field small { color: #8aa0c2; line-height: 1.4; }
.leader-concepts { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; font-size: 12px; color: #5b6b82; }
.mini-empty { color: #8aa0c2; font-size: 11px; }
.mini-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.mini-chip {
  border: 1px solid #d7e4f6;
  background: #f7faff;
  color: #3d5270;
  border-radius: 8px;
  padding: 0 6px;
  height: 18px;
  line-height: 16px;
  font-size: 11px;
  cursor: pointer;
  i { margin-left: 3px; font-style: normal; color: #8aa0c2; font-size: 10px; }
  &.main { border-color: #1a73e8; color: #1a73e8; background: #e8f1ff; }
  &.sub { border-color: #f0c36a; color: #8a5a00; background: #fff8eb; }
}
.assign-menu {
  position: fixed;
  z-index: 30;
  min-width: 132px;
  padding: 6px;
  border: 1px solid #d7e4f6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(26, 55, 96, 0.12);
}
.assign-title { font-size: 11px; color: #8aa0c2; padding: 2px 6px 4px; }
.assign-menu button {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  color: #24344d;
  font-size: 12px;
  line-height: 22px;
  padding: 0 6px;
  border-radius: 4px;
  cursor: pointer;
}
.assign-menu button:hover { background: #e8f1ff; color: #1a73e8; }
.btn { border-radius: 6px; padding: 7px 14px; font-size: 13px; cursor: pointer; }
.btn.ghost { background: #fff; border: 1px solid #d7e4f6; color: #3d5270; }
.btn.primary { background: #1a73e8; border: 1px solid #1a73e8; color: #fff; }
@media (max-width: 1100px) {
  .kb-shell { grid-template-columns: 1fr; height: auto; }
  .kb-rail { max-height: 420px; }
  .advice-row { grid-template-columns: 1fr; }
}
</style>
